import {
  Button as MuiButton,
  Dialog,
  DialogTitle,
  InputBase,
  Typography,
  DialogContent,
  Tooltip,
  DialogActions,
  styled,
} from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import { useProjectPrivateContext } from '../../../../context/projectContext'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
// import { createPortal } from 'react-dom' // DEBUG用
import { WbsItemSearchSearchConditionV2 } from '../../hooks/useSearchConditionV2'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { useOnSingleValueChangedGenerator } from './WbsItemSearchSearchHeader/useOnSingleValueChangedGenerator'
import { FocusableCellProps } from '../../../../components/tables/table-cells'
import { Button } from '../../../../components/buttons'
import {
  WbsItemStatus,
  WbsItemType,
} from '../../../../../domain/entity/WbsItemEntity'
import { SelectOption } from '../../../../model/selectOption'
import { intl } from '../../../../../i18n'
import CodeVO from '../../../../../domain/value-object/CodeVO'
import { customEnumRepository } from '../../../../../domain/repository'
import { CUSTOM_ENUM_NONE } from '../../../../../lib/commons/customEnum'
import { useWbsItemRepository } from '../../../../../services/adaptors/wbsItemRepositoryAdaptor'
import { useProjectMemberRepository } from '../../../../../services/adaptors/projectMemberRepositoryAdaptor'
import { ProjectMemberSearchOption } from '../../../../../domain/value-object/ProjectMemberSearchOption'
import { ProjectMemberSearchConditionAutocompleteInput } from './ProjectMemberSearchConditionAutocompleteInput'
import sprint, {
  SprintDetail,
  SprintStatus,
} from '../../../../../lib/functions/sprint'
import { useDebounce } from '../../../../hooks/useDebounce'
import DateTimeInput from '../../../../components/editors/input/DateTimeInput'
import DateTimeVO from '../../../../../domain/value-object/DateTimeVO'
import DateTimeTermVO from '../../../../../domain/value-object/DateTimeTermVO'
import { SearchConditionAndColumnStateV2 } from '../../../../../services/storage-services/ui-states/searchConditionAndColumnStateV2StorageService'
import { PermanentUiState } from '../../../../../services/model/uiState'
import { useDispatch, useSelector } from 'react-redux'
import { AllState } from '../../../../../store'
import { fetchTagsByProject } from '../../../../../store/tag'
import { TagForWbsItem } from '../../../../../lib/functions/tag'
import user, { UserDetail } from '../../../../../lib/functions/user'
import { WBS_ITEM_SEARCH_COUNT_LIMIT } from '../../wbsItemSearch'
import SaveSearchConditionListDialogV2 from './SaveSearchConditionListDialogV2'
import { FontSize } from '../../../../../styles/commonStyles'
import SearchConditionSelectBoxCheckmarks from '../../../../components/search-forms/search-panel/SearchConditionSelectBoxCheckmarks'
import { SearchConditionSprintSelect } from './SearchConditionSprintSelect'
import { TeamSprintGroup } from './sprintGroupTypes'
import ProcessScopeSearchSelect from './ProcessScopeSearchSelect'
import SearchConditionDate from '../../../../components/search-forms/search-panel/SearchConditionDate'
import SearchConditionChipInput from './SearchConditionChipInput'
import SearchConditionChipSelect from './SearchConditionChipSelect'
import { TextMatchQuantifier } from '../../../../../domain/value-object/WbsItemSearchConditionV2Vo'
// import { useWbsItemSearchConditionV2ApiRequestTransformService } from '../../../../../services/transform-service/wbsItemSearchConditionV2ApiRequestTransformService' // DEBUG用

type Props = {
  count: number
  total: number
  open: boolean
  onClose: () => void
  searchCondition: WbsItemSearchSearchConditionV2
  onChangeCondition: (updated: Partial<WbsItemSearchSearchConditionV2>) => void
  onEnter: (() => void) | undefined
  resetCondition: () => void
  search: () => void
  filteringInput: string
  setFilteringInput: (newValue: string) => void
  onSelectState: (
    selected: PermanentUiState<SearchConditionAndColumnStateV2>
  ) => void
  functionUuid: string
  teamOptions: SelectOption[]
}
const WbsSearchSearchConditionDialogV2 = ({
  count,
  total,
  open,
  onClose,
  onChangeCondition,
  searchCondition,
  onEnter,
  resetCondition,
  search,
  filteringInput,
  setFilteringInput,
  onSelectState,
  functionUuid,
  teamOptions,
}: Props) => {
  const { wbsItemTypes, ticketTypes, projectUuid } = useProjectPrivateContext()

  const { onChangeValueGenerator, onChangeBlankGenerator } =
    useOnSingleValueChangedGenerator(searchCondition, onChangeCondition)

  const onTeamUuidsChange = onChangeValueGenerator('teamUuids')
  const onSprintUuidsChange = onChangeValueGenerator('sprintUuids')
  const onTagConditionsChange = onChangeValueGenerator('tagUuids')

  const [processOptions, setProcessOptions] = useState<SelectOption[]>([])
  const [processParentMap, setProcessParentMap] = useState<
    Record<string, string>
  >({})
  const [processOptionsLoading, setProcessOptionsLoading] =
    useState<boolean>(false)
  const [priorityOptions, setPriorityOptions] = useState<SelectOption[]>([])
  const [tagOptions, setTagOptions] = useState<SelectOption[]>([])
  const [sprintOptions, setSprintOptions] = useState<SelectOption[]>([])
  const sprintCacheRef = useRef<Map<string, SprintDetail[]>>(new Map())
  const debouncedTeamUuids = useDebounce(searchCondition.teamUuids, 300)
  const { searchAsReferencedEntity, fetchListAsReferencedEntity } =
    useWbsItemRepository()
  const projectMemberRepository = useProjectMemberRepository()

  const [memberSearchOptions, setMemberSearchOptions] = useState<
    ProjectMemberSearchOption[]
  >([])
  useEffect(() => {
    if (!projectUuid || !open) return
    const fetchMembers = async () => {
      const result =
        await projectMemberRepository.searchAsProjectMemberSearchOption('')
      setMemberSearchOptions(result)
    }
    fetchMembers()
  }, [projectUuid, projectMemberRepository, open])

  useEffect(() => {
    if (!projectUuid || !open) return
    setProcessOptionsLoading(true)
    fetchListAsReferencedEntity(projectUuid, [
      WbsItemType.WORKGROUP,
      WbsItemType.PROCESS,
      WbsItemType.DELIVERABLE_LIST,
    ])
      .then(results => {
        setProcessOptions(
          results.map(item => ({
            value: item.uuid,
            name: item.name,
            iconUrl: item.iconUrl,
            tooltip: item.path,
          }))
        )
        setProcessParentMap(
          Object.fromEntries(
            results
              .filter(item => item.parentUuid)
              .map(item => [item.uuid, item.parentUuid!])
          )
        )
      })
      .finally(() => setProcessOptionsLoading(false))
  }, [projectUuid, fetchListAsReferencedEntity, open])

  const [openSavedSearchConditionPanel, setOpenSavedSearchConditionPanel] =
    useState<boolean>(false)

  const handleSavedSearchConditionPanelClose = () => {
    setOpenSavedSearchConditionPanel(false)
  }

  const handleSavedSearchConditionPanelOpen = () => {
    setOpenSavedSearchConditionPanel(true)
  }

  const fetchProjectMember = useCallback(async (value: string) => {
    try {
      const response = await user.getDetail({ uuid: value })
      const userDetail = response.json as UserDetail
      return {
        value: userDetail.uuid,
        name: userDetail.name,
        iconUrl: userDetail.iconUrl,
      }
    } catch (err) {
      return undefined
    }
  }, [])
  // TODO: SprintStatusDisplayOrder に置き換える
  const getSortIndex = (status: SprintStatus) => {
    const sortedStatusList = [
      SprintStatus.INPROGRESS,
      SprintStatus.STANDBY,
      SprintStatus.FINISHED,
      SprintStatus.CANCELED,
    ]
    return sortedStatusList.findIndex(v => v === status)
  }
  useEffect(() => {
    if (!debouncedTeamUuids || debouncedTeamUuids.length === 0) {
      sprintCacheRef.current.clear()
      setSprintOptions([])
      return
    }

    const cache = sprintCacheRef.current
    const currentUuids = new Set(debouncedTeamUuids)

    // 選択解除されたチームのキャッシュを削除
    for (const key of cache.keys()) {
      if (!currentUuids.has(key)) {
        cache.delete(key)
      }
    }

    // 未取得のチームだけ特定
    const newUuids = debouncedTeamUuids.filter(uuid => !cache.has(uuid))

    const buildOptions = () =>
      Array.from(cache.values())
        .flat()
        .sort((a, b) => getSortIndex(a.status) - getSortIndex(b.status))
        .map(v => ({
          value: v.uuid,
          name: v.name,
          groupBy: v.status,
        }))

    if (newUuids.length === 0) {
      // キャッシュのみで再構成（チーム削除時）
      setSprintOptions(buildOptions())
      return
    }

    // 新規チームのみフェッチ
    Promise.all(
      newUuids.map(async uuid => {
        const sprints = await sprint.searchByText(uuid)
        cache.set(uuid, sprints)
      })
    ).then(() => {
      setSprintOptions(buildOptions())
    })
  }, [debouncedTeamUuids])

  // チーム選択変更時に、削除されたチームのスプリントのみ選択解除する
  // sprintOptions が空かつチームが選択されている場合はフェッチ中なのでスキップ
  useEffect(() => {
    const selectedSprintUuids = searchCondition.sprintUuids
    if (!selectedSprintUuids || selectedSprintUuids.length === 0) return
    const hasTeams =
      searchCondition.teamUuids && searchCondition.teamUuids.length > 0
    if (hasTeams && sprintOptions.length === 0) return
    const validUuids = new Set(sprintOptions.map(o => o.value))
    const filtered = selectedSprintUuids.filter(uuid => validUuids.has(uuid))
    if (filtered.length < selectedSprintUuids.length) {
      onSprintUuidsChange(filtered.length > 0 ? filtered : undefined)
    }
    // sprintOptions のみを依存にし、スプリント候補の変化時のみ発火させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprintOptions])

  const teamSprintGroups: TeamSprintGroup[] = useMemo(() => {
    if (!debouncedTeamUuids || debouncedTeamUuids.length === 0) return []
    const cache = sprintCacheRef.current
    return debouncedTeamUuids
      .filter(uuid => cache.has(uuid))
      .map(uuid => ({
        teamUuid: uuid,
        teamName: teamOptions.find(t => t.value === uuid)?.name ?? uuid,
        sprints: cache.get(uuid)!,
      }))
    // sprintOptions を依存に含めることで、fetch完了時に再計算される
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTeamUuids, teamOptions, sprintOptions])

  const typesOptions = useMemo(() => {
    return wbsItemTypes.getAll().map(vo => ({
      name: vo.name,
      value: vo.code,
      iconUrl: vo.iconUrl,
    }))
  }, [wbsItemTypes])

  const ticketTypesOptions = useMemo(() => {
    const selectOptions: SelectOption[] = ticketTypes.map(t => ({
      name: t.name,
      value: t.code,
      iconUrl: t.iconUrl,
    }))
    selectOptions.push({
      name: intl.formatMessage({ id: 'none' }),
      value: 'NONE',
    })
    return selectOptions
  }, [ticketTypes])

  const statusOptions = useMemo(() => {
    return Object.keys(WbsItemStatus).map(k => ({
      name: k,
      value: k,
    }))
  }, [])

  useEffect(() => {
    if (!projectUuid) return
    const getAndSetOptions = async () => {
      const customEnum = await customEnumRepository.getByCodeAndGroupKeys(
        new CodeVO('WBS_PRIORITY'),
        [projectUuid]
      )
      const values = customEnum.values.map(v => v.toPlainValue())
      if (!values.some(v => v.value === CUSTOM_ENUM_NONE)) {
        values.push({
          name: intl.formatMessage({
            id: 'none',
          }),
          value: CUSTOM_ENUM_NONE,
          foregroundColor: undefined,
          backgroundColor: undefined,
          iconUrl: undefined,
        })
      }
      setPriorityOptions(values)
    }
    getAndSetOptions()
  }, [projectUuid])

  const tags = useSelector<AllState, TagForWbsItem[] | undefined>(state =>
    projectUuid ? state.tag[projectUuid] : undefined
  )
  useEffect(() => {
    if (!tags || tags.length === 0) return
    setTagOptions(
      tags.map(v => ({
        value: v.uuid,
        name: v.name,
        backgroundColor: v.backgroundColor,
      }))
    )
  }, [tags])
  const dispatch = useDispatch()
  useEffect(() => {
    if (!tags || tags.length === 0) {
      dispatch(fetchTagsByProject(projectUuid))
    }
  }, [projectUuid])

  const toggleQuantifier = useCallback(
    (
      field:
        | 'codeIncludeQuantifier'
        | 'codeExcludeQuantifier'
        | 'displayNameIncludeQuantifier'
        | 'displayNameExcludeQuantifier'
        | 'descriptionIncludeQuantifier'
        | 'descriptionExcludeQuantifier'
        | 'tagIncludeQuantifier'
        | 'tagExcludeQuantifier'
    ) => {
      const isExclude = field.includes('Exclude')
      const defaultQuantifier: TextMatchQuantifier = isExclude ? 'all' : 'any'
      const current: TextMatchQuantifier =
        searchCondition[field] ?? defaultQuantifier
      onChangeCondition({ [field]: current === 'any' ? 'all' : 'any' })
    },
    [searchCondition, onChangeCondition]
  )

  const isSearchable = useMemo(
    () => count <= WBS_ITEM_SEARCH_COUNT_LIMIT,
    [count]
  )

  // --- DEBUG: V2クエリ表示 ---
  // const searchConditionApiRequestTransformer =
  //   useWbsItemSearchConditionV2ApiRequestTransformService(projectUuid)
  // const debugQuery = useMemo(
  //   () =>
  //     searchConditionApiRequestTransformer.toV2ApiRequest(
  //       searchCondition,
  //       0,
  //       10000
  //     ),
  //   [searchConditionApiRequestTransformer, searchCondition]
  // )
  // const [showDebugQuery, setShowDebugQuery] = useState(false)
  // --- END DEBUG ---

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          '& .MuiPaper-root': { width: '800px' },
        }}
      >
        <DialogTitle sx={{ padding: '25px 24px 20px 30px' }}>
          <DialogHeaderRow>
            <Typography
              sx={{ color: colorPalette.monotone[10], fontSize: '15px' }}
            >
              {intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.condition',
              })}
            </Typography>
            <SearchCountBadge>
              <Typography
                sx={{
                  color: colorPalette.monotone[10],
                  fontSize: FontSize.LARGE,
                  fontWeight: 700,
                  margin: '0 4px',
                }}
              >
                {intl.formatMessage(
                  { id: 'wbs.search.header.search.hits' },
                  { hit: count }
                )}
              </Typography>
              <Typography
                sx={{
                  color: colorPalette.monotone[4],
                  fontSize: FontSize.MEDIUM,
                }}
              >
                /
              </Typography>
              <Typography
                sx={{
                  color: colorPalette.monotone[4],
                  fontSize: FontSize.MEDIUM,
                  fontWeight: 500,
                  marginLeft: '4px',
                }}
              >
                {intl.formatMessage(
                  { id: 'wbs.search.header.search.total' },
                  { total }
                )}
              </Typography>
            </SearchCountBadge>
            {!isSearchable && (
              <SearchLimitWarningLabel>
                <ErrorOutlineRoundedIcon
                  sx={{
                    color: colorPalette.pink[5],
                    fontSize: '16px',
                  }}
                />
                <Typography
                  sx={{
                    color: colorPalette.pink[5],
                    fontSize: FontSize.MEDIUM,
                    fontWeight: 500,
                    // 上ズレ防止のpadding
                    paddingTop: '4px',
                    paddingBottom: '-4px',
                  }}
                >
                  {intl.formatMessage(
                    {
                      id: 'wbsItemSearchFilterPanel.searchLimitWarning',
                    },
                    { limit: WBS_ITEM_SEARCH_COUNT_LIMIT }
                  )}
                </Typography>
              </SearchLimitWarningLabel>
            )}
            <Button
              colorPattern="monotone"
              variant="outlined"
              sx={{
                margin: 'auto 20px auto auto',
                cursor: 'pointer',
              }}
              onClick={onClose}
            >
              <ClearRoundedIcon />
              <Typography>
                {intl.formatMessage({ id: 'dialog.close' })}
              </Typography>
            </Button>
          </DialogHeaderRow>
        </DialogTitle>
        <DialogContent>
          <SearchConditionContainer>
            <ProcessScopeSearchSelect
              value={searchCondition.rootUuids}
              options={processOptions}
              parentMap={processParentMap}
              isLoading={processOptionsLoading}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.processScope',
              })}
              onChange={onChangeValueGenerator('rootUuids')}
            />
            <SearchConditionChipInput
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.displayName',
              })}
              placeholder={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.input.chipPlaceholder',
              })}
              value={searchCondition.displayName}
              onChange={onChangeValueGenerator('displayName')}
              includeQuantifier={searchCondition.displayNameIncludeQuantifier}
              excludeQuantifier={searchCondition.displayNameExcludeQuantifier}
              onToggleIncludeQuantifier={() =>
                toggleQuantifier('displayNameIncludeQuantifier')
              }
              onToggleExcludeQuantifier={() =>
                toggleQuantifier('displayNameExcludeQuantifier')
              }
            />
            <SearchConditionChipInput
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.code',
              })}
              placeholder={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.input.chipPlaceholder',
              })}
              value={searchCondition.code}
              onChange={onChangeValueGenerator('code')}
              includeQuantifier={searchCondition.codeIncludeQuantifier}
              excludeQuantifier={searchCondition.codeExcludeQuantifier}
              onToggleIncludeQuantifier={() =>
                toggleQuantifier('codeIncludeQuantifier')
              }
              onToggleExcludeQuantifier={() =>
                toggleQuantifier('codeExcludeQuantifier')
              }
              labelIds={{
                anyQuantifier: 'wbsItemSearch.quantifier.any.exact',
                allQuantifier: 'wbsItemSearch.quantifier.all.exact',
                includeMode: 'wbsItemSearch.quantifier.match',
                excludeMode: 'wbsItemSearch.quantifier.notMatch',
              }}
            />
            <SearchConditionChipInput
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.description',
              })}
              placeholder={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.input.chipPlaceholder',
              })}
              value={searchCondition.description}
              onChange={onChangeValueGenerator('description')}
              includeQuantifier={searchCondition.descriptionIncludeQuantifier}
              excludeQuantifier={searchCondition.descriptionExcludeQuantifier}
              onToggleIncludeQuantifier={() =>
                toggleQuantifier('descriptionIncludeQuantifier')
              }
              onToggleExcludeQuantifier={() =>
                toggleQuantifier('descriptionExcludeQuantifier')
              }
            />
            <SearchConditionSelectBoxCheckmarks
              value={searchCondition.types}
              options={typesOptions}
              onChange={onChangeValueGenerator('types')}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.types',
              })}
            />
            <SearchConditionSelectBoxCheckmarks
              value={searchCondition.ticketTypes}
              options={ticketTypesOptions}
              onChange={onChangeValueGenerator('ticketTypes')}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.ticketTypes',
              })}
            />
            <SearchConditionSelectBoxCheckmarks
              value={searchCondition.status}
              options={statusOptions}
              onChange={onChangeValueGenerator('status')}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.status',
              })}
            />
            <SearchConditionSelectBoxCheckmarks
              value={searchCondition.priority}
              options={priorityOptions}
              onChange={onChangeValueGenerator('priority')}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.priority',
              })}
            />
            <SearchConditionSelectBoxCheckmarks
              value={searchCondition.teamUuids}
              options={teamOptions}
              onChange={(newValue: string[] | string | undefined) => {
                const teamUuids = Array.isArray(newValue)
                  ? newValue.length > 0
                    ? newValue
                    : undefined
                  : newValue
                    ? [newValue]
                    : undefined
                onTeamUuidsChange(teamUuids)
              }}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.team',
              })}
            />
            <ProjectMemberSearchConditionAutocompleteInput
              value={searchCondition.accountableUuids}
              fetchSelectedOption={fetchProjectMember}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.accountable',
              })}
              onChange={onChangeValueGenerator('accountableUuids')}
              memberOptions={memberSearchOptions}
            />
            <ProjectMemberSearchConditionAutocompleteInput
              value={searchCondition.responsibleUuids}
              fetchSelectedOption={fetchProjectMember}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.responsible',
              })}
              onChange={onChangeValueGenerator('responsibleUuids')}
              memberOptions={memberSearchOptions}
            />
            <ProjectMemberSearchConditionAutocompleteInput
              value={searchCondition.assigneeUuids}
              fetchSelectedOption={fetchProjectMember}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.assignee',
              })}
              onChange={onChangeValueGenerator('assigneeUuids')}
              memberOptions={memberSearchOptions}
            />
            {searchCondition.teamUuids &&
            searchCondition.teamUuids.length > 0 ? (
              <SearchConditionSprintSelect
                value={searchCondition.sprintUuids}
                teamSprintGroups={teamSprintGroups}
                sprintOptions={sprintOptions}
                title={intl.formatMessage({
                  id: 'wbsItemSearchFilterPanel.sprint',
                })}
                onChange={onSprintUuidsChange}
              />
            ) : (
              <ConditionFieldContainer>
                <Typography
                  sx={{
                    color: colorPalette.monotone[4],
                    minWidth: '80px',
                    marginRight: '10px',
                  }}
                >
                  {intl.formatMessage({
                    id: 'wbsItemSearchFilterPanel.sprint',
                  })}
                </Typography>
                <Typography
                  sx={{
                    color: colorPalette.monotone[3],
                    margin: '10px 0 10px 10px',
                  }}
                >
                  {intl.formatMessage({
                    id: 'wbsItemSearchFilterPanel.selectTeam',
                  })}
                </Typography>
              </ConditionFieldContainer>
            )}
            <SearchConditionChipSelect
              value={searchCondition.tagUuids}
              options={tagOptions}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.tags',
              })}
              onChange={onTagConditionsChange}
              includeQuantifier={searchCondition.tagIncludeQuantifier}
              excludeQuantifier={searchCondition.tagExcludeQuantifier}
              onToggleIncludeQuantifier={() =>
                toggleQuantifier('tagIncludeQuantifier')
              }
              onToggleExcludeQuantifier={() =>
                toggleQuantifier('tagExcludeQuantifier')
              }
              triState={true}
            />
            <SearchConditionDate
              value={searchCondition.scheduledStartDate}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.scheduledStartDate',
              })}
              onChange={onChangeValueGenerator('scheduledStartDate')}
              isBlankSearchEnabled={true}
              blank={!!searchCondition.blank?.scheduledStartDate}
              onChangeBlank={onChangeBlankGenerator('scheduledStartDate')}
            />
            <SearchConditionDate
              value={searchCondition.scheduledEndDate}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.scheduledEndDate',
              })}
              onChange={onChangeValueGenerator('scheduledEndDate')}
              isBlankSearchEnabled={true}
              blank={!!searchCondition.blank?.scheduledEndDate}
              onChangeBlank={onChangeBlankGenerator('scheduledEndDate')}
            />
            <SearchConditionDate
              value={searchCondition.actualStartDate}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.actualStartDate',
              })}
              onChange={onChangeValueGenerator('actualStartDate')}
              isBlankSearchEnabled={true}
              blank={!!searchCondition.blank?.actualStartDate}
              onChangeBlank={onChangeBlankGenerator('actualStartDate')}
            />
            <SearchConditionDate
              value={searchCondition.actualEndDate}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.actualEndDate',
              })}
              onChange={onChangeValueGenerator('actualEndDate')}
              isBlankSearchEnabled={true}
              blank={!!searchCondition.blank?.actualEndDate}
              onChangeBlank={onChangeBlankGenerator('actualEndDate')}
            />
            <ProjectMemberSearchConditionAutocompleteInput
              value={searchCondition.createdByUuids}
              fetchSelectedOption={fetchProjectMember}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.createdBy',
              })}
              onChange={onChangeValueGenerator('createdByUuids')}
              memberOptions={memberSearchOptions}
            />
            <SearchConditionDateTime
              value={searchCondition.createdAt}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.createdAt',
              })}
              onChange={onChangeValueGenerator('createdAt')}
            />
            <ProjectMemberSearchConditionAutocompleteInput
              value={searchCondition.updatedByUuids}
              fetchSelectedOption={fetchProjectMember}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.updatedBy',
              })}
              onChange={onChangeValueGenerator('updatedByUuids')}
              memberOptions={memberSearchOptions}
            />
            <SearchConditionDateTime
              value={searchCondition.updatedAt}
              title={intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.updatedAt',
              })}
              onChange={onChangeValueGenerator('updatedAt')}
            />
          </SearchConditionContainer>
        </DialogContent>
        <StyledDialogActions>
          <DialogButtons>
            <Button
              size="m"
              colorPattern="monotone"
              variant="outlined"
              onClick={resetCondition}
            >
              <ClearRoundedIcon />
              <Typography>
                {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.reset' })}
              </Typography>
            </Button>
            <Button
              size="m"
              colorPattern="skyBlue"
              variant="outlined"
              onClick={handleSavedSearchConditionPanelOpen}
            >
              <Typography>
                {intl.formatMessage({
                  id: 'wbsItemSearchFilterPanel.saved.searchCondition',
                })}
              </Typography>
            </Button>
            <SaveSearchConditionListDialogV2
              open={openSavedSearchConditionPanel}
              handleSavedSearchConditionPanelClose={
                handleSavedSearchConditionPanelClose
              }
              filteringInput={filteringInput}
              setFilteringInput={setFilteringInput}
              onSelectState={onSelectState}
              functionUuid={functionUuid}
              searchCondition={searchCondition}
            />
            <Tooltip
              title={
                !isSearchable
                  ? intl.formatMessage({
                      id: 'wbsItemSearchFilterPanel.tooltip.searchLimit',
                    })
                  : ''
              }
            >
              <span>
                <Button
                  size="m"
                  colorPattern="skyBlue"
                  variant="filled"
                  onClick={search}
                  disabled={!isSearchable}
                >
                  <Typography>
                    {intl.formatMessage({
                      id: 'wbsItemSearchFilterPanel.search',
                    })}
                  </Typography>
                </Button>
              </span>
            </Tooltip>
          </DialogButtons>
        </StyledDialogActions>
      </Dialog>
      {/* --- DEBUG: V2クエリJSON 画面右下固定表示 ---
      {createPortal(
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            width: 500,
            maxHeight: showDebugQuery ? '90vh' : 'auto',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
            background: '#1e1e1e',
            color: '#d4d4d4',
            fontFamily: '"Noto sans mono", Courier, monospace',
            fontSize: '12px',
            overflow: 'hidden',
            '& *': { fontFamily: 'inherit' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 12px',
              background: '#333',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => setShowDebugQuery(prev => !prev)}
          >
            <span>{showDebugQuery ? '▼' : '▶'} V2 Query JSON</span>
            <span style={{ color: '#888' }}>DEBUG</span>
          </Box>
          {showDebugQuery && (
            <Box
              component="pre"
              sx={{
                margin: 0,
                padding: '12px',
                overflow: 'auto',
                flex: 1,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {JSON.stringify(debugQuery, null, 2)}
            </Box>
          )}
        </Box>,
        document.body
      )}
      --- END DEBUG --- */}
    </>
  )
}
const DialogHeaderRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
})

const SearchCountBadge = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  marginLeft: '20px',
  padding: '4px 12px',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '16px',
})

const SearchLimitWarningLabel = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginLeft: '8px',
  padding: '4px 10px',
  backgroundColor: colorPalette.pink[1],
  borderRadius: '4px',
})

const SearchConditionContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '0 20px 20px',
  height: '700px',
  gap: '10px',
})

const ConditionFieldContainer = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '5px 10px',
  height: '100%',
})

const StyledDialogActions = styled(DialogActions)({
  padding: '20px 10px',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '8px',
})
const DialogButtons = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  gap: '10px',
})

type SearchConditionBoxProps = {
  title: string
  placeholder?: string
  value?: string
  onChange: (value: string | undefined) => void
}

const SearchConditionTextBox = ({
  title,
  placeholder,
  value,
  onChange,
  onEnter,
  onBlur,
}: SearchConditionBoxProps & FocusableCellProps) => {
  const clear = useCallback(() => {
    onChange && onChange('')
  }, [])

  return (
    <TextBoxContainer>
      <Typography sx={{ color: colorPalette.monotone[4], minWidth: '80px' }}>
        {title}
      </Typography>
      <InputBase
        sx={{
          marginLeft: '20px',
          border: 'none',
          width: '400px',
          padding: '0 8px',
          color: colorPalette.monotone[10],
        }}
        value={value}
        placeholder={placeholder}
        onChange={event => {
          onChange(event.target.value ? event.target.value : '')
        }}
        onKeyPress={event => {
          if (onEnter && event.key === 'Enter') {
            onEnter()
          }
        }}
        onBlur={onBlur}
      />
      <MuiButton
        sx={{ minWidth: '20px', margin: '0 0 0 auto' }}
        onClick={clear}
      >
        <ClearRoundedIcon sx={{ color: colorPalette.monotone[4] }} />
      </MuiButton>
    </TextBoxContainer>
  )
}

type SearchConditionDateTimeProps = {
  value: DateTimeTermVO | undefined
  title: string
  onChange: (newValue: DateTimeTermVO | undefined) => void
}

const SearchConditionDateTime = ({
  value,
  title,
  onChange,
}: SearchConditionDateTimeProps) => {
  return (
    <ConditionFieldContainer>
      <Typography
        sx={{
          color: colorPalette.monotone[4],
          minWidth: '80px',
          marginRight: '10px',
        }}
      >
        {title}
      </Typography>
      <SearchConditionDateTimeCell value={value} onChange={onChange} />
    </ConditionFieldContainer>
  )
}

type SearchConditionDateTimeCellProps = {
  value: DateTimeTermVO | undefined
  onChange: (newValue: DateTimeTermVO | undefined) => void
}

const SearchConditionDateTimeCell = ({
  value,
  onChange,
}: SearchConditionDateTimeCellProps) => {
  const fromVo = useMemo(() => {
    const from = value && value.toPlainValue().startDateTime
    return from && new DateTimeVO(from)
  }, [value])

  const toVo = useMemo(() => {
    const to = value && value.toPlainValue().endDateTime
    return to && new DateTimeVO(to)
  }, [value])

  const clearFromDate = useCallback(() => {
    if (!value?.toPlainValue().endDateTime) {
      onChange(undefined)
      return
    }
    const newValue = new DateTimeTermVO(undefined, toVo)
    onChange(newValue)
  }, [onChange, toVo, value])

  const clearToDate = useCallback(() => {
    if (!value?.toPlainValue().startDateTime) {
      onChange(undefined)
      return
    }
    const newValue = new DateTimeTermVO(fromVo, undefined)
    onChange(newValue)
  }, [fromVo, onChange, value])

  const onChangeFrom = useCallback(
    (v: DateTimeVO | undefined) => {
      if (!v && !value?.toPlainValue().endDateTime) {
        onChange(undefined)
        return
      }
      if (!v && !toVo) {
        onChange(undefined)
        return
      }
      const newValue = new DateTimeTermVO(v, toVo)
      onChange(newValue)
    },
    [onChange, toVo, value]
  )

  const onChangeTo = useCallback(
    (v: DateTimeVO | undefined) => {
      if (!v && !value?.toPlainValue().startDateTime) {
        onChange(undefined)
        return
      }
      if (!v && !fromVo) {
        onChange(undefined)
        return
      }
      const newValue = new DateTimeTermVO(fromVo, v)
      onChange(newValue)
    },
    [fromVo, onChange, value]
  )

  return (
    <DateTimeCellContainer>
      <DateTimeInput
        placeholder={'YYYY/MM/DD HH:mm:ss'}
        value={fromVo}
        onChange={onChangeFrom}
      />
      <Button
        colorPattern="monotone"
        sx={{
          border: 'none',
          background: colorPalette.monotone[0],
          width: '10px',
          height: '24px',
          borderRadius: '15px',
          cursor: 'pointer',
        }}
        onClick={clearFromDate}
      >
        <ClearRoundedIcon
          sx={{
            color: colorPalette.monotone[4],
          }}
        />
      </Button>
      <Typography sx={{ margin: 'auto 10px', color: colorPalette.monotone[3] }}>
        ～
      </Typography>
      <DateTimeInput
        placeholder={'YYYY/MM/DD HH:mm:ss'}
        value={toVo}
        onChange={onChangeTo}
      />
      <Button
        colorPattern="monotone"
        sx={{
          border: 'none',
          background: colorPalette.monotone[0],
          width: '10px',
          height: '24px',
          borderRadius: '15px',
          cursor: 'pointer',
        }}
        onClick={clearToDate}
      >
        <ClearRoundedIcon
          sx={{
            color: colorPalette.monotone[4],
          }}
        />
      </Button>
    </DateTimeCellContainer>
  )
}

const TextBoxContainer = styled('div')({
  height: '40px',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '5px 10px',
})

const SelectedItemChip = styled('div')({
  display: 'flex',
  padding: '3px 5px',
  borderRadius: '15px',
  margin: '3px 5px',
  flexWrap: 'wrap',
  background: colorPalette.monotone[1],
})

const DateTimeCellContainer = styled('div')({
  marginLeft: '20px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

export default WbsSearchSearchConditionDialogV2
